// src/pages/Checkout.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Checkout.css';

export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const addressInputRef = useRef(null);
  const [cart, setCart] = useState([]);
  const [currentStep, setCurrentStep] = useState(1); // 1: Contact, 2: Delivery, 3: Payment, 4: Review
  const [autocompleteService, setAutocompleteService] = useState(null);
  
  // Contact Information
  const [contactInfo, setContactInfo] = useState({
    email: '',
    phone: '',
    firstName: '',
    lastName: ''
  });

  // Delivery Information
  const [deliveryInfo, setDeliveryInfo] = useState({
    address: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    deliveryDate: '',
    deliveryTime: '',
    instructions: ''
  });

  // Payment Information (placeholder - no real data)
  const [paymentInfo, setPaymentInfo] = useState({
    cardholderName: '',
    saveForFuture: false
  });

  useEffect(() => {
    // Get cart from location state or localStorage
    const cartData = location.state?.cart || JSON.parse(localStorage.getItem('checkout_cart') || '[]');
    if (cartData.length === 0) {
      // No items in cart, redirect to order page
      navigate('/order-now');
    }
    setCart(cartData);
  }, [location.state, navigate]);

  // Initialize Google Places Autocomplete when on delivery step
  useEffect(() => {
    if (currentStep === 2 && addressInputRef.current && !autocompleteService) {
      // Load Google Places API
      if (!window.google) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBe1QUTjviDLadu8E3XU9Gj7F4af3ceR04&libraries=places`;
        script.async = true;
        script.onload = () => initializeAutocomplete();
        document.head.appendChild(script);
      } else {
        initializeAutocomplete();
      }
    }
  }, [currentStep, autocompleteService]);

  const initializeAutocomplete = () => {
    if (!window.google || !addressInputRef.current) return;

    const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['address'],
      componentRestrictions: { country: ['us', 'ca'] } // US and Canada
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      console.log('📍 Address selected:', place);

      if (!place.address_components) return;

      // Extract address components
      let streetNumber = '';
      let route = '';
      let city = '';
      let state = '';
      let zipCode = '';

      place.address_components.forEach(component => {
        const types = component.types;
        if (types.includes('street_number')) streetNumber = component.long_name;
        if (types.includes('route')) route = component.long_name;
        if (types.includes('locality')) city = component.long_name;
        if (types.includes('administrative_area_level_1')) state = component.short_name;
        if (types.includes('postal_code')) zipCode = component.long_name;
      });

      // Auto-fill the form
      setDeliveryInfo({
        ...deliveryInfo,
        address: `${streetNumber} ${route}`.trim(),
        city: city,
        state: state,
        zipCode: zipCode
      });

      console.log('✅ Address auto-filled:', { streetNumber, route, city, state, zipCode });
    });

    setAutocompleteService(autocomplete);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.dish.price_cents * item.quantity), 0);
  };

  const getDeliveryFee = () => {
    return 599; // $5.99 delivery fee
  };

  const getTax = () => {
    const subtotal = getCartTotal();
    return Math.round(subtotal * 0.0825); // 8.25% tax
  };

  const getGrandTotal = () => {
    return getCartTotal() + getDeliveryFee() + getTax();
  };

  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactInfo.email || !contactInfo.phone || !contactInfo.firstName || !contactInfo.lastName) {
      alert('Please fill in all contact information');
      return;
    }
    setCurrentStep(2);
  };

  const handleDeliverySubmit = (e) => {
    e.preventDefault();
    if (!deliveryInfo.address || !deliveryInfo.city || !deliveryInfo.state || !deliveryInfo.zipCode || !deliveryInfo.deliveryDate || !deliveryInfo.deliveryTime) {
      alert('Please fill in all delivery information');
      return;
    }
    setCurrentStep(3);
  };

  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    if (!paymentInfo.cardholderName) {
      alert('Please enter cardholder name');
      return;
    }
    setCurrentStep(4);
  };

  const handlePlaceOrder = async () => {
    console.log('🛒 Placing order with data:', {
      contact: contactInfo,
      delivery: deliveryInfo,
      payment: paymentInfo,
      cart: cart,
      total: getGrandTotal()
    });

    // TODO: This is where we'll integrate Stripe and create the order
    alert('🎉 Order placed successfully! (Demo mode - no actual payment processed)');
    
    // Save order details to localStorage for tracking
    const orderData = {
      orderNumber: `ORD-${Date.now()}`,
      total: getGrandTotal(),
      customer: `${contactInfo.firstName} ${contactInfo.lastName}`,
      email: contactInfo.email,
      phone: contactInfo.phone,
      deliveryAddress: `${deliveryInfo.address}${deliveryInfo.apartment ? ', ' + deliveryInfo.apartment : ''}, ${deliveryInfo.city}, ${deliveryInfo.state} ${deliveryInfo.zipCode}`,
      deliveryDate: deliveryInfo.deliveryDate,
      deliveryTime: deliveryInfo.deliveryTime,
      deliveryInstructions: deliveryInfo.instructions,
      items: cart.map(item => ({
        name: item.dish.name,
        quantity: item.quantity,
        price: item.dish.price_cents
      })),
      placedAt: new Date().toISOString()
    };
    
    localStorage.setItem(`order_${orderData.orderNumber}`, JSON.stringify(orderData));
    
    // Clear cart and redirect to confirmation page
    localStorage.removeItem('checkout_cart');
    navigate('/order-confirmation', { 
      state: orderData
    });
  };

  const StepIndicator = () => (
    <div className="step-indicator">
      <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
        <div className="step-number">1</div>
        <div className="step-label">Contact</div>
      </div>
      <div className="step-line"></div>
      <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
        <div className="step-number">2</div>
        <div className="step-label">Delivery</div>
      </div>
      <div className="step-line"></div>
      <div className={`step ${currentStep >= 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
        <div className="step-number">3</div>
        <div className="step-label">Payment</div>
      </div>
      <div className="step-line"></div>
      <div className={`step ${currentStep >= 4 ? 'active' : ''}`}>
        <div className="step-number">4</div>
        <div className="step-label">Review</div>
      </div>
    </div>
  );

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h1 className="checkout-title">Checkout</h1>
        
        <StepIndicator />

        <div className="checkout-content">
          {/* Main Form Area */}
          <div className="checkout-form">
            {/* Step 1: Contact Information */}
            {currentStep === 1 && (
              <div className="checkout-step">
                <h2>Contact Information</h2>
                <form onSubmit={handleContactSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        id="firstName"
                        type="text"
                        value={contactInfo.firstName}
                        onChange={(e) => setContactInfo({...contactInfo, firstName: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        id="lastName"
                        type="text"
                        value={contactInfo.lastName}
                        onChange={(e) => setContactInfo({...contactInfo, lastName: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      id="email"
                      type="email"
                      value={contactInfo.email}
                      onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      id="phone"
                      type="tel"
                      value={contactInfo.phone}
                      onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      required
                    />
                  </div>
                  <button type="submit" className="btn-next">Continue to Delivery →</button>
                </form>
              </div>
            )}

            {/* Step 2: Delivery Information */}
            {currentStep === 2 && (
              <div className="checkout-step">
                <h2>Delivery Details</h2>
                <form onSubmit={handleDeliverySubmit}>
                  <div className="form-group">
                    <label htmlFor="address">Street Address * {autocompleteService && <span className="autocomplete-badge">🔍 Auto-complete enabled</span>}</label>
                    <input
                      ref={addressInputRef}
                      id="address"
                      type="text"
                      value={deliveryInfo.address}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, address: e.target.value})}
                      placeholder="Start typing your address..."
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="apartment">Apartment, Suite, etc.</label>
                    <input
                      id="apartment"
                      type="text"
                      value={deliveryInfo.apartment}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, apartment: e.target.value})}
                      placeholder="Apt 4B"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input
                        id="city"
                        type="text"
                        value={deliveryInfo.city}
                        onChange={(e) => setDeliveryInfo({...deliveryInfo, city: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="state">State *</label>
                      <select
                        id="state"
                        value={deliveryInfo.state}
                        onChange={(e) => setDeliveryInfo({...deliveryInfo, state: e.target.value})}
                        required
                      >
                        <option value="">Select State</option>
                        <option value="TX">Texas</option>
                        <option value="MA">Massachusetts</option>
                        <option value="NY">New York</option>
                        <option value="IL">Illinois</option>
                        <option value="LA">Louisiana</option>
                        <option value="FL">Florida</option>
                        <option value="ON">Ontario</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="zipCode">ZIP Code *</label>
                      <input
                        id="zipCode"
                        type="text"
                        value={deliveryInfo.zipCode}
                        onChange={(e) => setDeliveryInfo({...deliveryInfo, zipCode: e.target.value})}
                        placeholder="12345"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="deliveryDate">Delivery Date *</label>
                      <input
                        id="deliveryDate"
                        type="date"
                        value={deliveryInfo.deliveryDate}
                        onChange={(e) => setDeliveryInfo({...deliveryInfo, deliveryDate: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="deliveryTime">Order Now - Delivery Time *</label>
                      <select
                        id="deliveryTime"
                        value={deliveryInfo.deliveryTime}
                        onChange={(e) => setDeliveryInfo({...deliveryInfo, deliveryTime: e.target.value})}
                        required
                      >
                        <option value="">Select Time</option>
                        <option value="11:00">11:00 AM</option>
                        <option value="11:30">11:30 AM</option>
                        <option value="12:00">12:00 PM</option>
                        <option value="12:30">12:30 PM</option>
                        <option value="13:00">1:00 PM</option>
                        <option value="13:30">1:30 PM</option>
                        <option value="14:00">2:00 PM</option>
                        <option value="14:30">2:30 PM</option>
                        <option value="15:00">3:00 PM</option>
                        <option value="15:30">3:30 PM</option>
                        <option value="16:00">4:00 PM</option>
                        <option value="16:30">4:30 PM</option>
                        <option value="17:00">5:00 PM</option>
                        <option value="17:30">5:30 PM</option>
                        <option value="18:00">6:00 PM</option>
                        <option value="18:30">6:30 PM</option>
                        <option value="19:00">7:00 PM</option>
                        <option value="19:30">7:30 PM</option>
                        <option value="20:00">8:00 PM</option>
                        <option value="20:30">8:30 PM</option>
                        <option value="21:00">9:00 PM</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="instructions">Delivery Instructions</label>
                    <textarea
                      id="instructions"
                      value={deliveryInfo.instructions}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, instructions: e.target.value})}
                      placeholder="Any special delivery instructions..."
                      rows="3"
                    />
                  </div>
                  <div className="button-group">
                    <button type="button" className="btn-back" onClick={() => setCurrentStep(1)}>← Back</button>
                    <button type="submit" className="btn-next">Continue to Payment →</button>
                  </div>
                </form>
              </div>
            )}

            {/* Step 3: Payment Information */}
            {currentStep === 3 && (
              <div className="checkout-step">
                <h2>Payment Information</h2>
                <div className="payment-notice">
                  <p>🔒 Secure payment powered by Stripe</p>
                  <p className="demo-notice">Demo Mode: No actual payment will be processed</p>
                </div>
                <form onSubmit={handlePaymentSubmit}>
                  <div className="form-group">
                    <label htmlFor="cardholderName">Cardholder Name *</label>
                    <input
                      id="cardholderName"
                      type="text"
                      value={paymentInfo.cardholderName}
                      onChange={(e) => setPaymentInfo({...paymentInfo, cardholderName: e.target.value})}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  
                  {/* Stripe Card Element Placeholder */}
                  <div className="stripe-placeholder">
                    <div className="form-group">
                      <label>Card Information *</label>
                      <div className="mock-stripe-element">
                        <div className="stripe-card-number">
                          <span className="placeholder-text">💳 Card number (Stripe element will go here)</span>
                        </div>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="mock-stripe-element">
                        <span className="placeholder-text">MM / YY</span>
                      </div>
                      <div className="mock-stripe-element">
                        <span className="placeholder-text">CVC</span>
                      </div>
                    </div>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={paymentInfo.saveForFuture}
                        onChange={(e) => setPaymentInfo({...paymentInfo, saveForFuture: e.target.checked})}
                      />
                      <span>Save payment method for future orders</span>
                    </label>
                  </div>

                  <div className="button-group">
                    <button type="button" className="btn-back" onClick={() => setCurrentStep(2)}>← Back</button>
                    <button type="submit" className="btn-next">Review Order →</button>
                  </div>
                </form>
              </div>
            )}

            {/* Step 4: Review & Confirm */}
            {currentStep === 4 && (
              <div className="checkout-step">
                <h2>Review Your Order</h2>
                
                <div className="review-section">
                  <h3>Contact Information</h3>
                  <p>{contactInfo.firstName} {contactInfo.lastName}</p>
                  <p>{contactInfo.email}</p>
                  <p>{contactInfo.phone}</p>
                  <button className="edit-btn" onClick={() => setCurrentStep(1)}>Edit</button>
                </div>

                <div className="review-section">
                  <h3>Delivery Details</h3>
                  <p>{deliveryInfo.address} {deliveryInfo.apartment}</p>
                  <p>{deliveryInfo.city}, {deliveryInfo.state} {deliveryInfo.zipCode}</p>
                  <p><strong>Date:</strong> {new Date(deliveryInfo.deliveryDate).toLocaleDateString()}</p>
                  <p><strong>Time:</strong> {deliveryInfo.deliveryTime}</p>
                  {deliveryInfo.instructions && <p><strong>Instructions:</strong> {deliveryInfo.instructions}</p>}
                  <button className="edit-btn" onClick={() => setCurrentStep(2)}>Edit</button>
                </div>

                <div className="review-section">
                  <h3>Payment Method</h3>
                  <p>💳 {paymentInfo.cardholderName}</p>
                  <p className="demo-text">Demo: Card ending in ••••4242</p>
                  <button className="edit-btn" onClick={() => setCurrentStep(3)}>Edit</button>
                </div>

                <div className="review-section">
                  <h3>Order Items</h3>
                  {cart.map(item => (
                    <div key={item.dish.id} className="review-item">
                      <span className="item-name">{item.quantity}x {item.dish.name}</span>
                      <span className="item-price">{formatPrice(item.dish.price_cents * item.quantity)}</span>
                    </div>
                  ))}
                </div>

                <div className="button-group">
                  <button type="button" className="btn-back" onClick={() => setCurrentStep(3)}>← Back</button>
                  <button type="button" className="btn-place-order" onClick={handlePlaceOrder}>
                    🛒 Place Order - {formatPrice(getGrandTotal())}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="order-summary">
            <h3>Order Summary</h3>
            <div className="summary-items">
              {cart.map(item => (
                <div key={item.dish.id} className="summary-item">
                  <div className="summary-item-info">
                    <span className="item-qty">{item.quantity}x</span>
                    <span className="item-name">{item.dish.name}</span>
                  </div>
                  <span className="item-price">{formatPrice(item.dish.price_cents * item.quantity)}</span>
                </div>
              ))}
            </div>
            
            <div className="summary-totals">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>{formatPrice(getCartTotal())}</span>
              </div>
              <div className="summary-row">
                <span>Delivery Fee</span>
                <span>{formatPrice(getDeliveryFee())}</span>
              </div>
              <div className="summary-row">
                <span>Tax</span>
                <span>{formatPrice(getTax())}</span>
              </div>
              <div className="summary-row total">
                <span>Total</span>
                <span>{formatPrice(getGrandTotal())}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

